/** Version metadata returned by the app-version-info endpoint. */
export interface AppVersionInfo {
  app: string;
  version: string;
  commitHash?: string;
}

const APP_VERSION_INFO_PATH = '/app-version-info';

/**
 * Validates and normalizes the app-version-info JSON payload.
 * @returns Normalized version info or null when the payload is invalid.
 */
export function parseAppVersionInfo(value: unknown): AppVersionInfo | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }

  const app = 'app' in value ? value.app : undefined;
  const version = 'version' in value ? value.version : undefined;
  const commitHash = 'commit_hash' in value ? value.commit_hash : undefined;

  if (
    typeof app !== 'string' ||
    typeof version !== 'string' ||
    version === ''
  ) {
    return null;
  }

  const info: AppVersionInfo = { app, version };

  if (typeof commitHash === 'string') {
    info.commitHash = commitHash;
  }

  return info;
}

/**
 * Returns true when remote version metadata differs from the loaded baseline.
 */
export function isVersionStale(
  baseline: Pick<AppVersionInfo, 'version' | 'commitHash'>,
  remote: AppVersionInfo,
): boolean {
  if (remote.version !== baseline.version) {
    return true;
  }

  if (
    baseline.commitHash &&
    remote.commitHash &&
    remote.commitHash !== baseline.commitHash
  ) {
    return true;
  }

  return false;
}

/**
 * Fetches the currently deployed app version from the endpoint.
 * @returns Parsed version info or null when the request or payload is invalid.
 */
export async function fetchAppVersionInfo(
  appName: string,
): Promise<AppVersionInfo | null> {
  if (!appName) {
    return null;
  }

  const url = `${APP_VERSION_INFO_PATH}?${new URLSearchParams({ app: appName })}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const data: unknown = await response.json();

    return parseAppVersionInfo(data);
  } catch {
    return null;
  }
}
